import React from 'react';
import { Link } from 'react-router-dom';
import { companyInfo, aboutText, values, solutions, benefits, whyTransition, partners } from '../data/mockData';
import { Zap, TrendingDown, Shield, TrendingUp, Home as HomeIcon, BadgeSwissFranc } from 'lucide-react';

const HomePage = () => {
  // Order: Panneaux solaires, Batteries, Pompes à chaleur, Boilers
  const mainSolutions = [
    solutions.find(s => s.id === 'solar'),
    solutions.find(s => s.id === 'batteries'),
    solutions.find(s => s.id === 'heat-pumps'),
    solutions.find(s => s.id === 'thermodynamic-boilers')
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section - Plus compact */}
      <section className="relative pt-24 pb-12 bg-gradient-to-br from-[#03224c] via-[#03224c]/95 to-[#03224c]/90">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center text-white">
            <div className="inline-block px-4 py-1.5 bg-[#e57200] rounded-full mb-4">
              <span className="text-white font-semibold text-sm">
                Solutions complètes pour votre transition énergétique en Suisse romande
              </span>
            </div>
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold leading-tight mb-4">
              Votre transition énergétique simplifiée
            </h1>
            <p className="text-lg text-gray-200 mb-6 leading-relaxed max-w-3xl mx-auto">
              {aboutText.intro}
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link to="/contact">
                <button className="px-8 py-3 bg-[#e57200] text-white rounded-lg font-bold hover:bg-[#e57200]/90 transition-all shadow-lg">
                  DEVIS GRATUIT
                </button>
              </Link>
              <Link to="/solutions">
                <button className="px-8 py-3 bg-white text-[#03224c] rounded-lg font-bold hover:bg-gray-100 transition-all">
                  NOS SOLUTIONS
                </button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* About Section - Compact */}
      <section className="py-12 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-8 items-center">
            <div>
              <h2 className="text-3xl font-bold text-[#03224c] mb-4">À propos</h2>
              <p className="text-gray-700 mb-3 leading-relaxed">
                {aboutText.intro}
              </p>
              <p className="text-gray-700 mb-4 leading-relaxed">
                {aboutText.vision}
              </p>
              <div className="flex flex-col sm:flex-row gap-3">
                <Link to="/about">
                  <button className="px-6 py-2.5 border-2 border-[#03224c] text-[#03224c] rounded-lg font-semibold hover:bg-[#03224c] hover:text-white transition-all">
                    EN SAVOIR PLUS
                  </button>
                </Link>
                <Link to="/contact">
                  <button className="px-6 py-2.5 bg-[#e57200] text-white rounded-lg font-semibold hover:bg-[#e57200]/90 transition-all">
                    DEMANDER UN DEVIS
                  </button>
                </Link>
              </div>
            </div>
            <div>
              <img
                src="https://images.unsplash.com/photo-1613665813446-82a78c468a1d"
                alt="Installation solaire"
                className="rounded-xl shadow-xl w-full h-80 object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Quote Section - Compact */}
      <section className="py-8 bg-gradient-to-r from-[#e57200] to-[#e57200]/90">
        <div className="container mx-auto px-4">
          <p className="text-xl md:text-2xl font-bold text-white text-center italic max-w-4xl mx-auto">
            "{aboutText.quote}"
          </p>
        </div>
      </section>

      {/* Values - Compact avec fond */}
      <section className="py-12 bg-gradient-to-br from-gray-50 to-white">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-[#03224c] text-center mb-8">
            Des valeurs de confiance et d'engagement
          </h2>
          <div className="grid md:grid-cols-3 gap-6">
            {values.map((value, index) => (
              <div key={index} className="bg-white p-6 rounded-xl shadow-md hover:shadow-lg transition-all border-l-4 border-[#e57200]">
                <h3 className="text-xl font-bold text-[#03224c] mb-2">{value.title}</h3>
                <p className="text-gray-600 text-sm leading-relaxed">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Solutions Grid - Plus compact */}
      <section className="py-12 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold text-[#03224c] mb-3">
              Nos solutions énergétiques
            </h2>
            <p className="text-gray-600">
              Des systèmes complets pour votre indépendance énergétique
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {mainSolutions.map((solution) => (
              <div key={solution.id} className="group bg-white rounded-xl shadow-md hover:shadow-2xl transition-all duration-300 overflow-hidden border border-gray-100">
                <div className="h-40 overflow-hidden">
                  <img
                    src={solution.image}
                    alt={solution.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                </div>
                <div className="p-5">
                  <h3 className="text-lg font-bold text-[#03224c] mb-2">{solution.title}</h3>
                  <p className="text-sm text-gray-600 mb-3 line-clamp-2">{solution.shortDesc}</p>
                  <Link to="/solutions" className="inline-flex items-center text-[#e57200] font-semibold text-sm">
                    Découvrir →
                  </Link>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center mt-8">
            <Link to="/solutions">
              <button className="px-8 py-3 bg-[#03224c] text-white rounded-lg font-bold hover:bg-[#03224c]/90 transition-all shadow-lg">
                TOUTES NOS SOLUTIONS
              </button>
            </Link>
          </div>
        </div>
      </section>

      {/* Partners - Compact */}
      <section className="py-8 bg-gray-50 border-y border-gray-200">
        <div className="container mx-auto px-4">
          <div className="flex flex-wrap justify-center items-center gap-12">
            <div className="h-16 flex items-center">
              <img src="/images/brands/sunpower.png" alt="Sunpower" className="h-12 w-auto object-contain grayscale hover:grayscale-0 transition-all" />
            </div>
            <div className="h-16 flex items-center">
              <img src="/images/brands/enphase.png" alt="Enphase" className="h-12 w-auto object-contain grayscale hover:grayscale-0 transition-all" />
            </div>
            <div className="h-16 flex items-center">
              <img src="/images/brands/atmoce.png" alt="Atmoce" className="h-12 w-auto object-contain grayscale hover:grayscale-0 transition-all" />
            </div>
            <div className="h-16 flex items-center">
              <img src="/images/brands/vaillant.png" alt="Vaillant" className="h-12 w-auto object-contain grayscale hover:grayscale-0 transition-all" />
            </div>
            <div className="h-16 flex items-center">
              <img src="/images/brands/panasonic.png" alt="Panasonic" className="h-12 w-auto object-contain grayscale hover:grayscale-0 transition-all" />
            </div>
          </div>
        </div>
      </section>

      {/* Why Transition - Compact avec fond */}
      <section className="py-12 bg-gradient-to-br from-blue-50 to-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold text-[#03224c] mb-3">
              Pourquoi enclencher votre transition ?
            </h2>
            <p className="text-gray-600 max-w-3xl mx-auto leading-relaxed">
              {whyTransition.intro}
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5 mb-6">
            {benefits.map((benefit, index) => {
              const icons = [Zap, TrendingDown, Shield, TrendingUp, HomeIcon, BadgeSwissFranc];
              const IconComponent = icons[index];
              
              return (
                <div key={index} className="bg-white p-5 rounded-xl shadow-md hover:shadow-lg transition-all border-t-4 border-[#e57200]">
                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0 w-10 h-10 bg-[#e57200] rounded-lg flex items-center justify-center">
                      <IconComponent size={20} className="text-white" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-bold text-[#03224c] mb-2 text-lg">{benefit.title}</h3>
                      <p className="text-sm text-gray-600 leading-relaxed">{benefit.description}</p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="bg-[#e57200] text-white p-5 rounded-xl text-center max-w-3xl mx-auto">
            <p className="font-bold text-lg">
              {whyTransition.conclusion}
            </p>
          </div>
        </div>
      </section>

      {/* CTA Final - Compact */}
      <section className="py-12 bg-gradient-to-r from-[#03224c] to-[#03224c]/90">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center text-white">
            <h2 className="text-3xl font-bold mb-4">
              Prêt à démarrer votre projet ?
            </h2>
            <p className="text-lg mb-6 text-gray-200">
              Contactez-nous pour une étude personnalisée et gratuite. Notre équipe vous accompagne de A à Z pour votre projet de rénovation énergétique.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link to="/contact">
                <button className="px-8 py-3 bg-[#e57200] text-white rounded-lg font-bold hover:bg-[#e57200]/90 transition-all shadow-lg">
                  DEVIS GRATUIT
                </button>
              </Link>
              <a href="tel:021391789">
                <button className="px-8 py-3 border-2 border-white text-white rounded-lg font-bold hover:bg-white/10 transition-all">
                  021 391 78 99
                </button>
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
